/*
 * Product card C lists – cached page safety (same idea as the 60-day check in workshop-page.js).
 * The server sorts and filters by date at render time. If the page is served from cache after a
 * workshop has started, hide it from upcoming lists ([data-ws-upcoming]) and move "Nästa workshop"
 * to the first remaining plain workshop card. Past lists are left as rendered.
 */
(() => {
  function daysUntil(isoDate) {
    const [y, m, d] = isoDate.split('-').map(Number);
    const target = Date.UTC(y, m - 1, d);
    const now = new Date();
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((target - today) / 86400000);
  }

  function refresh(list) {
    const badge = list.querySelector('[data-ws-next-badge]');
    let removed = false;
    list.querySelectorAll('.ws-card[data-ws-start]').forEach((card) => {
      if (daysUntil(card.dataset.wsStart) < 0) {
        (card.closest('[data-ws-card-item]') || card).remove();
        removed = true;
      }
    });
    if (!removed || !badge) return;

    const first = list.querySelector('.ws-card[data-ws-plain]');
    if (first && !first.contains(badge)) first.querySelector('.ws-card__media')?.appendChild(badge);
  }

  function init() {
    document.querySelectorAll('[data-ws-upcoming]').forEach(refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/*
 * Past workshops carousel ([data-ws-carousel]): the arrows scroll one card at a time and are
 * disabled at the ends. Touch/trackpad scrolling works natively (scroll-snap in CSS).
 */
(() => {
  function initCarousel(root) {
    const track = root.querySelector('[data-ws-carousel-track]');
    const prev = root.querySelector('[data-ws-carousel-prev]');
    const next = root.querySelector('[data-ws-carousel-next]');
    if (!track || !prev || !next) return;

    const step = () => {
      const item = track.querySelector('.ws-carousel__item');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max;
      root.classList.toggle('ws-carousel--static', max <= 0);
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function init() {
    document.querySelectorAll('[data-ws-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/*
 * Artist page gallery, images after the even grid ([data-ws-gallery], hidden until the
 * first click): they keep their own ratio and go into the shortest column (3 on desktop, 2 on mobile).
 * "Visa fler" ([data-ws-gallery-more]) shows six more each time.
 */
(() => {
  const STEP = 6;
  const mobile = window.matchMedia('(max-width: 749px)');

  function initGallery(root) {
    const items = [...root.querySelectorAll('.ws-artist-page__gallery-item')];
    const button = root.parentElement.querySelector('[data-ws-gallery-more]');
    if (!items.length) {
      button?.parentElement.remove();
      root.remove();
      return;
    }
    let columns = [];
    let heights = [];

    const ratio = (item) => {
      const img = item.querySelector('img');
      const w = Number(img?.getAttribute('width'));
      const h = Number(img?.getAttribute('height'));
      return w && h ? h / w : 1;
    };

    const place = (item) => {
      const i = heights.indexOf(Math.min(...heights));
      columns[i].appendChild(item);
      heights[i] += ratio(item);
    };

    const build = () => {
      const count = mobile.matches ? 2 : 3;
      root.querySelectorAll('.ws-artist-page__gallery-col').forEach((col) => col.remove());
      columns = Array.from({ length: count }, () => {
        const col = document.createElement('div');
        col.className = 'ws-artist-page__gallery-col';
        root.appendChild(col);
        return col;
      });
      heights = columns.map(() => 0);
      items.forEach((item) => {
        if (item.hidden) root.appendChild(item);
        else place(item);
      });
      root.classList.add('is-masonry');
    };

    build();
    mobile.addEventListener('change', build);

    button?.addEventListener('click', () => {
      root.hidden = false;
      const next = items.filter((item) => item.hidden).slice(0, STEP);
      next.forEach((item) => {
        item.hidden = false;
        place(item);
      });
      if (!items.some((item) => item.hidden)) button.parentElement.remove();
    });
  }

  /*
   * The even grid ([data-ws-gallery-even], the first six images): some portrait images get a tall tile
   * two rows high and every column must end level. With C columns (3 desktop, 2 mobile), n images and
   * t tall tiles that needs (n + t) divisible by C. When the six alone give no tall tile, the grid
   * borrows one or two images from the "Visa fler" list so stående and liggande still mix.
   * Other portrait images stay in a normal 4:3 cell. With fewer than six images the bottom may not be level.
   */
  function tallCountFor(items, count) {
    const portraits = items.filter((item) => item.hasAttribute('data-portrait')).length;
    for (let t = portraits; t >= 0; t -= 1) {
      const rows = (items.length + t) / count;
      if (Number.isInteger(rows) && rows >= 2 * Math.ceil(t / count)) return t;
    }
    return 0;
  }

  function initEvenGrid(grid) {
    const more = grid.parentElement.querySelector('[data-ws-gallery]');
    const extras = more ? [...more.querySelectorAll('.ws-artist-page__gallery-item')] : [];
    let items = [...grid.querySelectorAll('.ws-artist-page__gallery-item')];
    const count = () => (mobile.matches ? 2 : 3);

    if (items.length >= 6 && tallCountFor(items, count()) === 0) {
      for (let extra = 1; extra <= 2 && extra <= extras.length; extra += 1) {
        const candidate = items.concat(extras.slice(0, extra));
        if (tallCountFor(candidate, count()) > 0) {
          candidate.slice(items.length).forEach((item) => {
            item.hidden = false;
            grid.appendChild(item);
          });
          items = candidate;
          break;
        }
      }
    }

    const layout = () => {
      const cols = count();
      const t = tallCountFor(items, cols);
      const rows = Math.ceil((items.length + t) / cols);
      const tall = new Set(items.filter((item) => item.hasAttribute('data-portrait')).slice(0, t));
      const columns = Array.from({ length: cols }, () => ({ talls: [], shorts: [], height: 0 }));

      items.filter((item) => tall.has(item)).forEach((item, i) => {
        columns[i % cols].talls.push(item);
        columns[i % cols].height += 2;
      });
      let c = 0;
      items.filter((item) => !tall.has(item)).forEach((item) => {
        let tries = 0;
        while (columns[c].height >= rows && tries < cols) {
          c = (c + 1) % cols;
          tries += 1;
        }
        columns[c].shorts.push(item);
        columns[c].height += 1;
        c = (c + 1) % cols;
      });

      columns.forEach((column, index) => {
        let row = 1;
        let tallTurn = index % 2 === 0;
        while (column.talls.length || column.shorts.length) {
          const useTall = column.talls.length > 0 && (tallTurn || !column.shorts.length);
          const item = useTall ? column.talls.shift() : column.shorts.shift();
          const span = useTall ? 2 : 1;
          item.style.gridColumn = String(index + 1);
          item.style.gridRow = `${row} / span ${span}`;
          row += span;
          tallTurn = !tallTurn;
        }
      });
    };

    layout();
    mobile.addEventListener('change', layout);
  }

  function init() {
    // The even grid first: it may borrow images from the "Visa fler" list.
    document.querySelectorAll('[data-ws-gallery-even]').forEach(initEvenGrid);
    document.querySelectorAll('[data-ws-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
